/**
 * Enhanced Multi-Column Slider Component
 * Features: Responsive breakpoints, touch support, accessibility, performance optimizations
 */

class MultiColumnSlider {
  constructor(element) {
    this.section = element;
    this.sectionId = element.dataset.sectionId;
    this.grid = element.querySelector('.columns-grid');
    this.columns = element.querySelectorAll('.column');
    this.dots = [];
    this.arrows = {
      prev: element.querySelector('.nav-arrow-prev'),
      next: element.querySelector('.nav-arrow-next')
    };
    this.dotsContainer = element.querySelector('.nav-dots');
    
    // Settings
    this.settings = {
      desktop: parseInt(element.dataset.columnsDesktop) || 3,
      tablet: parseInt(element.dataset.columnsTablet) || 2,
      mobile: parseInt(element.dataset.columnsMobile) || 1,
      sliderMobile: element.dataset.sliderMobile === 'true',
      sliderTablet: element.dataset.sliderTablet === 'true',
      autoplay: element.dataset.autoplay === 'true',
      autoplaySpeed: (parseInt(element.dataset.autoplaySpeed) * 1000) || 4000
    };
    
    // State
    this.currentSlide = 0;
    this.totalSlides = this.columns.length;
    this.slidesPerView = this.getSlidesPerView();
    this.maxSlides = Math.max(0, this.totalSlides - this.slidesPerView);
    this.autoplayInterval = null;
    this.isSliderActive = false;
    this.touchStart = 0;
    this.touchEnd = 0;
    
    this.init();
  }

  init() {
    this.checkSliderActive();
    this.setupDots();
    this.setupEventListeners();
    this.setupAccessibility();
    
    // Remove loading state with staggered animation
    setTimeout(() => {
      this.section.classList.remove('loading');
    }, 100);
    
    if (this.settings.autoplay && this.isSliderActive) {
      this.startAutoplay();
    }
    
    this.setupIntersectionObserver();
  }

  checkSliderActive() {
    const viewport = this.getViewport();
    this.isSliderActive = (
      (viewport === 'mobile' && this.settings.sliderMobile) ||
      (viewport === 'tablet' && this.settings.sliderTablet)
    );
    
    this.slidesPerView = this.getSlidesPerView();
    this.maxSlides = Math.max(0, this.totalSlides - this.slidesPerView);
    
    // Update navigation visibility
    const navigation = this.section.querySelector('.slider-navigation');
    if (navigation) {
      navigation.style.display = this.isSliderActive && this.totalSlides > this.slidesPerView ? 'block' : 'none';
    }
  }

  getViewport() {
    const width = window.innerWidth;
    if (width <= 749) return 'mobile';
    if (width <= 989) return 'tablet';
    return 'desktop';
  }

  getSlidesPerView() {
    const viewport = this.getViewport();
    switch (viewport) {
      case 'mobile': return this.settings.mobile;
      case 'tablet': return this.settings.tablet;
      default: return this.settings.desktop;
    }
  }

  setupDots() {
    if (!this.dotsContainer || !this.isSliderActive) return;
    
    this.dotsContainer.innerHTML = '';
    this.dots = [];
    
    const totalPages = Math.ceil(this.totalSlides / this.slidesPerView);
    
    for (let i = 0; i < totalPages; i++) {
      const dot = document.createElement('button');
      dot.className = 'nav-dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      dot.dataset.slide = i;
      
      if (i === 0) dot.classList.add('active');
      
      dot.addEventListener('click', () => this.goToSlide(i));
      
      this.dotsContainer.appendChild(dot);
      this.dots.push(dot);
    }
  }

  setupEventListeners() {
    // Arrow navigation
    if (this.arrows.prev) {
      this.arrows.prev.addEventListener('click', () => this.previousSlide());
    }
    
    if (this.arrows.next) {
      this.arrows.next.addEventListener('click', () => this.nextSlide());
    }
    
    // Keyboard navigation
    this.section.addEventListener('keydown', (e) => {
      if (!this.isSliderActive) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.previousSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextSlide();
          break;
        case 'Home':
          e.preventDefault();
          this.goToSlide(0);
          break;
        case 'End':
          e.preventDefault();
          this.goToSlide(this.dots.length - 1);
          break;
      }
    });
    
    // Touch events for mobile
    if (this.grid) {
      this.setupTouchEvents();
      this.setupScrollEvents();
    }
    
    // Resize handling
    window.addEventListener('resize', this.debounce(() => {
      this.handleResize();
    }, 250));
    
    // Autoplay pause on interaction
    this.setupAutoplayControls();
  }

  setupTouchEvents() {
    this.grid.addEventListener('touchstart', (e) => {
      this.touchStart = e.touches[0].clientX;
      this.stopAutoplay();
    }, { passive: true });
    
    this.grid.addEventListener('touchmove', (e) => {
      this.touchEnd = e.touches[0].clientX;
    }, { passive: true });
    
    this.grid.addEventListener('touchend', () => {
      this.handleTouchEnd();
    }, { passive: true });
  }

  setupScrollEvents() {
    let scrollTimeout;
    
    this.grid.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.updateDotsFromScroll();
      }, 100);
    }, { passive: true });
  }

  setupAccessibility() {
    // Add ARIA roles
    if (this.grid) {
      this.grid.setAttribute('role', 'region');
      this.grid.setAttribute('aria-label', 'Content slider');
    }
    
    if (this.dotsContainer) {
      this.dotsContainer.setAttribute('role', 'tablist');
    }
    
    // Add focus management
    this.columns.forEach((column, index) => {
      column.setAttribute('tabindex', index === 0 ? '0' : '-1');
      column.setAttribute('role', 'group');
      column.setAttribute('aria-label', `Item ${index + 1} of ${this.totalSlides}`);
    });
  }

  setupAutoplayControls() {
    // Pause on hover
    this.section.addEventListener('mouseenter', () => {
      this.stopAutoplay();
    });
    
    this.section.addEventListener('mouseleave', () => {
      if (this.settings.autoplay && this.isSliderActive) {
        this.startAutoplay();
      }
    });
    
    // Pause on focus
    this.section.addEventListener('focusin', () => {
      this.stopAutoplay();
    });
    
    this.section.addEventListener('focusout', () => {
      if (this.settings.autoplay && this.isSliderActive) {
        this.startAutoplay();
      }
    });
  }

  handleTouchEnd() {
    if (!this.touchStart || !this.touchEnd) return;
    
    const difference = this.touchStart - this.touchEnd;
    const threshold = 50;
    
    if (Math.abs(difference) > threshold) {
      if (difference > 0) {
        this.nextSlide();
      } else {
        this.previousSlide();
      }
    }
    
    this.touchStart = 0;
    this.touchEnd = 0;
    
    if (this.settings.autoplay && this.isSliderActive) {
      setTimeout(() => this.startAutoplay(), 2000);
    }
  }

  updateDotsFromScroll() {
    if (!this.isSliderActive || this.dots.length === 0) return;
    
    const scrollLeft = this.grid.scrollLeft;
    const itemWidth = this.columns[0]?.offsetWidth || 0;
    const gap = 20; // Approximate gap
    const slideWidth = itemWidth + gap;
    
    const currentIndex = Math.round(scrollLeft / slideWidth / this.slidesPerView);
    const clampedIndex = Math.max(0, Math.min(currentIndex, this.dots.length - 1));
    
    this.updateDots(clampedIndex);
    this.updateArrows(clampedIndex);
  }

  goToSlide(slideIndex, smooth = true) {
    if (!this.isSliderActive || !this.grid) return;
    
    const clampedIndex = Math.max(0, Math.min(slideIndex, this.dots.length - 1));
    
    const itemWidth = this.columns[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.grid).gap) || 20;
    const slideWidth = itemWidth + gap;
    const scrollPosition = clampedIndex * slideWidth * this.slidesPerView;
    
    this.grid.scrollTo({
      left: scrollPosition,
      behavior: smooth ? 'smooth' : 'auto'
    });
    
    this.currentSlide = clampedIndex;
    this.updateDots(clampedIndex);
    this.updateArrows(clampedIndex);
    this.updateFocus(clampedIndex);
  }

  nextSlide() {
    const nextIndex = Math.min(this.currentSlide + 1, this.dots.length - 1);
    this.goToSlide(nextIndex);
  }

  previousSlide() {
    const prevIndex = Math.max(this.currentSlide - 1, 0);
    this.goToSlide(prevIndex);
  }

  updateDots(activeIndex) {
    this.dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  updateArrows(activeIndex) {
    if (this.arrows.prev) {
      this.arrows.prev.disabled = activeIndex === 0;
      this.arrows.prev.setAttribute('aria-disabled', activeIndex === 0 ? 'true' : 'false');
    }
    
    if (this.arrows.next) {
      const isLast = activeIndex >= this.dots.length - 1;
      this.arrows.next.disabled = isLast;
      this.arrows.next.setAttribute('aria-disabled', isLast ? 'true' : 'false');
    }
  }

  updateFocus(activeIndex) {
    const startIndex = activeIndex * this.slidesPerView;
    
    this.columns.forEach((column, index) => {
      const isInView = index >= startIndex && index < startIndex + this.slidesPerView;
      column.setAttribute('tabindex', isInView ? '0' : '-1');
    });
  }

  startAutoplay() {
    if (!this.settings.autoplay || !this.isSliderActive || this.dots.length <= 1) return;
    
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      const nextIndex = this.currentSlide >= this.dots.length - 1 ? 0 : this.currentSlide + 1;
      this.goToSlide(nextIndex);
    }, this.settings.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  handleResize() {
    this.checkSliderActive();
    this.setupDots();
    this.goToSlide(0, false); // Reset to first slide without animation
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.settings.autoplay && this.isSliderActive) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(this.section);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  destroy() {
    this.stopAutoplay();
    // Remove event listeners if needed for cleanup
  }
}

// Initialize sliders
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('.multicolumn-slider');
  const sliderInstances = new Map();
  
  sliders.forEach(slider => {
    const instance = new MultiColumnSlider(slider);
    sliderInstances.set(slider.dataset.sectionId, instance);
  });
  
  // Shopify theme editor support
  if (typeof window.Shopify?.designMode !== 'undefined') {
    document.addEventListener('shopify:section:load', (event) => {
      const slider = event.target.querySelector('.multicolumn-slider');
      if (slider) {
        const instance = new MultiColumnSlider(slider);
        sliderInstances.set(slider.dataset.sectionId, instance);
      }
    });
    
    document.addEventListener('shopify:section:unload', (event) => {
      const sectionId = event.detail.sectionId;
      const instance = sliderInstances.get(sectionId);
      if (instance) {
        instance.destroy();
        sliderInstances.delete(sectionId);
      }
    });
    
    document.addEventListener('shopify:block:select', (event) => {
      const slider = event.target.closest('.multicolumn-slider');
      if (slider) {
        const instance = sliderInstances.get(slider.dataset.sectionId);
        if (instance) {
          const blockIndex = Array.from(slider.querySelectorAll('.column')).indexOf(event.target);
          const slideIndex = Math.floor(blockIndex / instance.slidesPerView);
          instance.goToSlide(slideIndex);
        }
      }
    });
  }
});